import type { MenuItem } from '@/components/layout/sidebar';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface RecentPage {
	path: string;
	titleKey: string;
	timestamp: number;
}

const RECENT_PAGES_KEY = 'vectro-recent-pages';
const MAX_RECENT_PAGES = 10;

// Flatten menu items to get all searchable pages
const flattenMenuItems = (items: MenuItem[]): MenuItem[] => {
	const result: MenuItem[] = [];

	const flatten = (item: MenuItem) => {
		if (item.path) {
			result.push(item);
		}
		if (item.children) {
			item.children.forEach(flatten);
		}
	};

	items.forEach(flatten);
	return result;
};

// Get recent pages from localStorage
const getRecentPages = (): RecentPage[] => {
	try {
		const stored = localStorage.getItem(RECENT_PAGES_KEY);
		if (stored) {
			return JSON.parse(stored);
		}
	} catch (error) {
		console.error('Failed to load recent pages:', error);
	}
	return [];
};

// Save recent pages to localStorage
const saveRecentPages = (pages: RecentPage[]): void => {
	try {
		localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(pages));
	} catch (error) {
		console.error('Failed to save recent pages:', error);
	}
};

// Add a page to recent pages
const addRecentPage = (path: string, titleKey: string): void => {
	const recentPages = getRecentPages();

	// Remove existing entry if present
	const filtered = recentPages.filter(page => page.path !== path);

	// Add new entry at the beginning
	const updated = [
		{ path, titleKey, timestamp: Date.now() },
		...filtered
	].slice(0, MAX_RECENT_PAGES);

	saveRecentPages(updated);
};

interface UsePageSearchOptions {
	menuItems: MenuItem[];
}

export const usePageSearch = ({ menuItems }: UsePageSearchOptions) => {
	const { t, i18n } = useTranslation('navigation');
	const navigate = useNavigate();
	const location = useLocation();
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [recentPagesVersion, setRecentPagesVersion] = useState(0);

	// Flatten menu items for search
	const searchableItems = useMemo(() => flattenMenuItems(menuItems), [menuItems]);

	// Get recent pages
	const recentPages = useMemo(() => {
		const recent = getRecentPages();
		// Filter to only include pages that still exist in menu
		return recent.filter(page =>
			searchableItems.some(item => item.path === page.path)
		);
	}, [searchableItems, location.pathname, recentPagesVersion]);

	// Track page visits
	useEffect(() => {
		const currentPath = location.pathname;
		const matchingItem = searchableItems.find(item => item.path === currentPath);

		if (matchingItem && matchingItem.path) {
			addRecentPage(matchingItem.path, matchingItem.titleKey);
			setRecentPagesVersion(prev => prev + 1);
		}
	}, [location.pathname, searchableItems]);

	// Filter items based on search query
	const filteredItems = useMemo(() => {
		if (!searchQuery.trim()) {
			return [];
		}

		const query = searchQuery.toLowerCase();

		const matchingItems = searchableItems.filter(item => {
			const translatedTitle = t(item.titleKey).toLowerCase();
			const englishTitle = i18n.t(item.titleKey, { lng: 'en' }).toLowerCase();
			const titleKeyParts = item.titleKey.toLowerCase().split('.');
			const lastPart = titleKeyParts[titleKeyParts.length - 1].replace(/_/g, ' ');

			return (
				translatedTitle.includes(query) ||
				englishTitle.includes(query) ||
				lastPart.includes(query)
			);
		});

		// Sort by recent usage
		return matchingItems.sort((a, b) => {
			const aRecentIndex = recentPages.findIndex(page => page.path === a.path);
			const bRecentIndex = recentPages.findIndex(page => page.path === b.path);

			if (aRecentIndex !== -1 && bRecentIndex !== -1) {
				return aRecentIndex - bRecentIndex;
			}

			if (aRecentIndex !== -1 && bRecentIndex === -1) {
				return -1;
			}
			if (aRecentIndex === -1 && bRecentIndex !== -1) {
				return 1;
			}

			const aTitle = t(a.titleKey).toLowerCase();
			const bTitle = t(b.titleKey).toLowerCase();
			return aTitle.localeCompare(bTitle);
		});
	}, [searchQuery, searchableItems, recentPages, t, i18n]);

	// Determine what to show in dropdown
	const displayItems = useMemo(() => {
		if (searchQuery.trim()) {
			return filteredItems;
		}
		return recentPages
			.map(page => searchableItems.find(i => i.path === page.path))
			.filter((item): item is MenuItem => item !== undefined);
	}, [searchQuery, filteredItems, recentPages, searchableItems]);

	// Reset selected index when display items change
	useEffect(() => {
		setSelectedIndex(0);
	}, [displayItems]);

	// Handle navigation
	const handleNavigate = (item: MenuItem) => {
		if (item.path) {
			navigate(item.path);
			setSearchQuery('');
		}
	};

	return {
		searchQuery,
		setSearchQuery,
		selectedIndex,
		setSelectedIndex,
		displayItems,
		filteredItems,
		recentPages,
		searchableItems,
		handleNavigate,
		isShowingRecent: !searchQuery.trim() && displayItems.length > 0
	};
};
